import React from 'react'
import { useDispatch } from 'react-redux'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '../../common/Tooltip'

import { AdPod, AdPodStatus, Role, User } from 'common/api/v1/types'
import { pluralize, useConfirmationDialog, useUser } from '../../../utils'
import { AppDispatch } from '../../../store'

import { UrlParamFilteredSearchBar } from '../../common/Filters/FilterView/FilteredSearchBar'
import Box from '@mui/material/Box'
import { Theme } from '@mui/material/styles'
import { deleteAdPods } from '../../../redux/actions/adPodActions'
import { FilterBy, FilterTemplate } from '../../common/Filters/FilterView/FilterTemplate'
import { AdPodsRequestParams } from '../../../api/nm-types'
import { format } from 'date-fns'
import { DATE_FORMAT_LONGER, sentenceCase } from 'common/api/v1/helpers'
import { dateFilterPossibleValues } from '../../common/Filters/FilterView/dateFilter'
import DeleteIcon from '@mui/icons-material/Delete'

interface CommonActionsProps {
  selected: Array<AdPod['id']>
  setSelected: React.Dispatch<React.SetStateAction<string[]>>
  adPods: AdPod[]
  user: User
}

const BatchDeleteButton = ({
  selected,
  setSelected,
  disabledMessage,
}: {
  selected: AdPod[]
  setSelected: React.Dispatch<React.SetStateAction<string[]>>
  disabledMessage?: string
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const showConfirmation = useConfirmationDialog()

  const onClick = () => {
    showConfirmation(
      () => {
        dispatch(deleteAdPods(selected.map(({ id }) => id)))
        setSelected([])
      },
      'Are you sure you want to remove the selected ad pods?',
      selected.length === 1 ? selected[0].name : 'remove ad pods',
      {
        ok: { text: 'remove', color: 'error' },
      },
    )
  }
  return (
    <Tooltip title={disabledMessage ?? 'Remove selected ad pods'} disablePortal={false}>
      <span>
        <IconButton aria-label="batch delete" onClick={onClick} disabled={!!disabledMessage}>
          <DeleteIcon />
        </IconButton>
      </span>
    </Tooltip>
  )
}

const CommonActions = ({ adPods, selected, setSelected, user }: CommonActionsProps) => {
  const { group } = useUser()
  const adPodsMaps = adPods.reduce<{ [key: string]: AdPod }>((acc, item) => ({ ...acc, [item.id]: item }), {})
  const showButtons = user.role === Role.super || selected.every((id) => adPodsMaps[id]?.groupId === group)
  const isOwnerOfAllSelected = selected.every((id) => adPodsMaps[id]?.createdBy === user.id)

  const filters = makeAdPodFilters()
  return (
    <Toolbar disableGutters style={{ overflowX: 'clip' }}>
      <Box
        sx={{
          transition: (theme: Theme) =>
            theme.transitions.create('flex-shrink', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.shortest,
            }),
        }}
        style={{
          flexShrink: selected.length > 0 ? 1 : 0,
          flexGrow: 1,
          flexBasis: '100%',
        }}
      >
        <UrlParamFilteredSearchBar
          urlParamCacheKey={'adPods'}
          mapFilterToUrlParamFn={mapAdPodFilterToUrlParam}
          mapUrlParamToFilterFn={mapUrlParamToAdPodFilter}
          searchTokenFilters={filters}
          rawTextFilter={filters.find((f) => f.key === AdPodFilterKey.name)!}
        />
      </Box>

      <Box
        sx={{
          transition: (theme: Theme) =>
            // Fade out, i.e. keep visible until contents has left the screen
            theme.transitions.create('visibility', {
              delay: selected.length === 0 ? theme.transitions.duration.shortest : 0,
            }),
        }}
        style={{
          marginLeft: 16,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          visibility: selected.length > 0 ? 'visible' : 'hidden',
        }}
      >
        <div style={{ width: 180, display: 'flex', justifyContent: 'center' }}>
          <Typography variant="subtitle1">{pluralize(selected.length, 'ad pod')} selected</Typography>
        </div>

        {showButtons && (
          <BatchDeleteButton
            selected={adPods.filter((d) => selected.includes(d.id))}
            setSelected={setSelected}
            disabledMessage={
              user.role === Role.super || isOwnerOfAllSelected
                ? undefined
                : 'You are not the owner of a selected ad pod'
            }
          />
        )}
      </Box>
    </Toolbar>
  )
}

enum AdPodFilterKey {
  name = 'name',
  timestamp = 'timestamp',
  status = 'status',
}

function mapAdPodFilterToUrlParam(filter: FilterBy): Partial<AdPodsRequestParams> {
  if ('value' in filter) {
    switch (filter.key) {
      case AdPodFilterKey.name:
        return { filter: filter.value }
      case AdPodFilterKey.timestamp:
        return filter.operator === 'after'
          ? { fromDate: new Date(filter.value).toISOString() }
          : { toDate: new Date(filter.value).toISOString() }
      case AdPodFilterKey.status:
        return { status: filter.value as AdPodStatus }
    }
  }
  return {}
}

function mapUrlParamToAdPodFilter(key: keyof AdPodsRequestParams, value: string): FilterBy | undefined {
  switch (key) {
    case 'filter':
      return {
        key: AdPodFilterKey.name,
        operator: 'matches',
        value,
      }
    case 'fromDate':
      return {
        key: AdPodFilterKey.timestamp,
        operator: 'after',
        value: format(new Date(value), DATE_FORMAT_LONGER),
      }
    case 'toDate':
      return {
        key: AdPodFilterKey.timestamp,
        operator: 'before',
        value: format(new Date(value), DATE_FORMAT_LONGER),
      }
    case 'status':
      return {
        key: AdPodFilterKey.status,
        operator: 'is',
        value: value as AdPodStatus,
        displayName: sentenceCase(value),
      }
  }
  return undefined
}

function makeAdPodFilters(): FilterTemplate[] {
  const filterTemplates: FilterTemplate[] = [
    { key: AdPodFilterKey.name, operator: 'matches' },
    {
      key: AdPodFilterKey.timestamp,
      operator: 'after',
      possibleValuesSync: (s) => dateFilterPossibleValues(s),
    },
    {
      key: AdPodFilterKey.timestamp,
      operator: 'before',
      possibleValuesSync: (s) => dateFilterPossibleValues(s),
    },
    {
      key: AdPodFilterKey.status,
      operator: 'is',
      possibleValuesSync: () =>
        Object.values(AdPodStatus).map((status) => ({
          value: status,
          displayName: sentenceCase(status),
        })),
    },
  ]
  return filterTemplates.sort((a, b) => a.key.localeCompare(b.key))
}

export default CommonActions
